/**
 * AccessControl - Simple RBAC and feature-gated tab filtering
 *
 * Roles supported: 'admin', 'tuner', 'creator', 'viewer'
 * Tabs can specify `rolesAllowed` and optional `featureFlag`.
 */

import Config, { isFeatureEnabled } from './Config.js';

/**
 * Get current user roles.
 * - DEV default: ['admin'] unless overridden
 * - PROD default: ['viewer']
 * - Override via localStorage key `userRoles` (comma-separated)
 */
export function getUserRoles() {
  try {
    // Local override
    const override = (typeof localStorage !== 'undefined')
      ? (localStorage.getItem('userRoles') || localStorage.getItem('userRole') || '')
      : '';
    if (override) {
      return String(override)
        .split(',')
        .map(s => s.trim().toLowerCase())
        .filter(Boolean);
    }
  } catch(_) {}

  // Defaults by env
  if (import.meta.env?.DEV || Config.APP_ENV === 'development') return ['admin'];
  return ['viewer'];
}

/**
 * Check if the current user can view a tab
 */
export function canViewTab(tab, roles = null) {
  // Feature flag gate (optional)
  if (tab.featureFlag && !isFeatureEnabled(tab.featureFlag)) return false;

  // RBAC gate
  const currentRoles = roles || getUserRoles();
  const allowed = Array.isArray(tab.rolesAllowed) && tab.rolesAllowed.length > 0
    ? tab.rolesAllowed.map(r => String(r).toLowerCase())
    : null; // null = unrestricted
  if (!allowed) return true;
  return currentRoles.some(r => allowed.includes(r));
}

/**
 * Filter tabs array to only those visible for current user
 */
export function filterTabs(tabs) {
  // If RBAC feature is off, return all feature-enabled tabs
  const rbacEnabled = Config.FEATURES?.RBAC === true;
  const roles = rbacEnabled ? getUserRoles() : null;
  return (tabs || []).filter(tab => {
    // Always respect featureFlag even when RBAC disabled
    if (tab.featureFlag && !isFeatureEnabled(tab.featureFlag)) return false;
    return rbacEnabled ? canViewTab(tab, roles) : true;
  });
}

export default { getUserRoles, canViewTab, filterTabs };

